import { Link } from "react-router-dom";
import Button from "@/components/base/Button";
import { clinicInfo } from "@/mocks/site";
import { serviceAreas } from "@/data/serviceAreas";

const stats = [
  { value: "2", label: "Specialist care areas" },
  { value: "7", label: "Dedicated services" },
  { value: "10+", label: "Years of experience" },
];

/**
 * Large, colourful Services hero. The layered anatomical composition and the
 * two glass area links make it immediately clear that Spine Care and
 * Orthopaedic Care are the practice's two primary service areas.
 */
export default function ServicesHero() {
  return (
    <section className="relative overflow-hidden bg-background-50">
      <div
        className="absolute inset-0 bg-gradient-to-b from-background-50 via-background-50 to-background-100"
        aria-hidden="true"
      ></div>
      <div
        className="absolute inset-0 opacity-[0.35] [background-image:linear-gradient(to_right,oklch(var(--background-200))_1px,transparent_1px),linear-gradient(to_bottom,oklch(var(--background-200))_1px,transparent_1px)] [background-size:72px_72px]"
        aria-hidden="true"
      ></div>
      <div className="absolute -right-24 -top-20 h-[30rem] w-[30rem] rounded-full bg-accent-100/60 blur-3xl" aria-hidden="true"></div>
      <div className="absolute -bottom-40 -left-24 h-[26rem] w-[26rem] rounded-full bg-primary-100/60 blur-3xl" aria-hidden="true"></div>
      <div className="absolute left-1/3 top-1/4 h-72 w-72 rounded-full bg-secondary-200/30 blur-3xl" aria-hidden="true"></div>

      <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-10 md:px-6 md:pt-12">
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            <li>
              <Link to="/" className="transition-colors hover:text-primary-700">
                Home
              </Link>
            </li>
            <li className="flex items-center gap-2">
              <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
              <span className="text-foreground-800">Services</span>
            </li>
          </ol>
        </nav>

        <div className="mt-8 grid grid-cols-1 items-center gap-x-12 gap-y-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <span className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50 px-3.5 py-1.5 font-label text-xs font-semibold uppercase tracking-[0.16em] text-accent-700">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500" aria-hidden="true"></span>
              Services
            </span>

            <h1 className="animate-fade-up mt-6 font-heading text-[2.4rem] font-semibold leading-[1.05] tracking-tight text-foreground-950 md:text-5xl lg:text-[3.4rem]">
              Specialist services for your{" "}
              <span className="bg-gradient-to-r from-primary-600 via-accent-500 to-secondary-500 bg-clip-text text-transparent">
                spine &amp; orthopaedics
              </span>
            </h1>

            <p className="animate-fade-up mt-6 max-w-xl font-body text-base leading-relaxed text-foreground-600 md:text-lg">
              Ortho Spine Care brings together two specialist care areas under one roof — Spine Care and Orthopaedic
              Care — supported by accurate diagnosis, non-surgical options first, and advanced surgery when it is
              needed.
            </p>

            <div className="animate-fade-up mt-8 flex flex-col gap-3 sm:flex-row">
              <Button to="/appointment" size="lg" icon="ri-calendar-check-line">
                Book an Appointment
              </Button>
              <Button href={clinicInfo.phoneHref} variant="outline" size="lg" icon="ri-phone-fill">
                Call Now
              </Button>
            </div>

            <div className="animate-fade-up mt-9 flex max-w-lg flex-wrap gap-x-8 gap-y-5">
              {stats.map((stat) => (
                <div key={stat.label} className="flex flex-col">
                  <span className="font-heading text-2xl font-semibold text-primary-700">{stat.value}</span>
                  <span className="mt-1 font-label text-xs uppercase tracking-wide text-foreground-500">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative mx-auto w-full max-w-[480px]">
              <div className="absolute -inset-x-5 -bottom-6 top-10 rounded-[2.5rem] bg-accent-100/70" aria-hidden="true"></div>
              <div
                className="absolute -right-3 -top-6 h-24 w-24 rounded-full border border-accent-200 bg-accent-50/50"
                aria-hidden="true"
              ></div>
              <div className="absolute -left-5 bottom-28 h-16 w-16 rounded-full bg-primary-100" aria-hidden="true"></div>

              <div className="relative overflow-hidden rounded-[2.5rem] border border-background-200 bg-background-100 shadow-[0_35px_80px_-45px_rgba(15,40,80,0.5)]">
                <div className="h-[440px] w-full md:h-[560px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Stylized%20three%20dimensional%20medical%20render%20of%20a%20human%20spine%20and%20a%20knee%20joint%20on%20a%20clean%20studio%20surface%20with%20soft%20royal%20blue%20cyan%20and%20violet%20gradient%20lighting%2C%20premium%20healthcare%20visual%2C%20no%20people&width=1200&height=1400&seq=ospc-services-hero-2026&orientation=portrait"
                    alt="Anatomical spine and joint render representing the Spine Care and Orthopaedic Care services"
                    title="Spine and orthopaedic services at Ortho Spine Care"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div
                  className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-primary-950/70 to-transparent"
                  aria-hidden="true"
                ></div>

                <div className="absolute inset-x-3 bottom-3 grid grid-cols-2 gap-2">
                  {serviceAreas.map((area) => (
                    <Link
                      key={area.slug}
                      to={area.href}
                      className="group flex items-center gap-2 rounded-xl border border-background-200 bg-background-50/95 px-3 py-2.5 backdrop-blur transition-colors hover:border-accent-300"
                    >
                      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-700">
                        <i
                          className={area.tone === "spine" ? "ri-body-scan-line" : "ri-focus-3-line"}
                          aria-hidden="true"
                        ></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate font-heading text-xs font-semibold text-foreground-950">
                          {area.eyebrow}
                        </span>
                        <span className="block font-label text-[0.65rem] text-foreground-500">View service</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}